import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Alert, AlertDescription } from '../../ui/alert';
import {
  Mail, Send, AlertTriangle, Eye, EyeOff, Loader2, Bell, Gift, Share2, Printer,
  RefreshCw, Building, CreditCard, Megaphone, Users, CheckCircle, XCircle, Save,
} from 'lucide-react';

/**
 * Tab "marketing" – ausgelagerte Tab-Komponente.
 * Erhält alle benötigten State- und Handler-Referenzen vom Parent via Props.
 */
const SettingsMarketing = ({ ctx }) => {
  const { t } = useTranslation();
  const {
    settings, setSettings, handleInputChange,
    showSmtpPassword, setShowSmtpPassword,
    testEmailTo, setTestEmailTo, sendingTest, testResult, handleTestEmail,
    triggeringFollowUp, followUpResult, handleTriggerFollowUp,
    triggeringBackup, backupResult, handleTriggerBackup,
  } = ctx;

  return (
    <>

          {/* Next Booking Discount Settings */}
          <Card className="card-shadow mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Printer className="w-5 h-5 text-cyan-500" />
                Druckservice
              </CardTitle>
              <CardDescription>
                Preis für den Druck- & Montageservice. Kunden können bei der Buchung wählen, ob sie selbst drucken oder den Service nutzen.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Druckservice anbieten</Label>
                </div>
                <button
                  type="button"
                  onClick={() => handleInputChange({ target: { name: 'print_service_enabled', value: !settings.print_service_enabled } })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.print_service_enabled ? 'bg-[hsl(var(--primary))]' : 'bg-[hsl(var(--muted))]'
                  }`}
                  data-testid="settings-print-service-toggle"
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.print_service_enabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
              {settings.print_service_enabled && (
                <div className="pt-3 border-t border-[hsl(var(--border))] space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="print_service_price" className="text-sm">Preis (CHF)</Label>
                    <Input
                      id="print_service_price"
                      name="print_service_price"
                      type="number"
                      step="0.01"
                      value={settings.print_service_price}
                      onChange={handleInputChange}
                      placeholder="180.00"
                      data-testid="settings-print-service-price"
                    />
                    <span className="text-xs text-[hsl(var(--muted-foreground))]">
                      Wird dem Kunden bei der Buchung als Option angezeigt.
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Next Booking Discount Settings */}
          <Card className="card-shadow mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gift className="w-5 h-5 text-amber-500" />
                Nächste-Buchung-Rabatt
              </CardTitle>
              <CardDescription>
                Kunden erhalten nach Auftragsabschluss (Foto-Bestätigung) automatisch einen Rabattcode für die nächste Buchung.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Nächste-Buchung-Rabatt aktivieren</Label>
                </div>
                <button
                  type="button"
                  onClick={() => handleInputChange({ target: { name: 'next_booking_discount_enabled', value: !settings.next_booking_discount_enabled } })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.next_booking_discount_enabled ? 'bg-[hsl(var(--primary))]' : 'bg-[hsl(var(--muted))]'
                  }`}
                  data-testid="settings-next-discount-toggle"
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.next_booking_discount_enabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
              {settings.next_booking_discount_enabled && (
                <div className="pt-3 border-t border-[hsl(var(--border))] space-y-3">
                  <div className="flex items-center gap-3">
                    <select
                      value={settings.next_booking_discount_type}
                      onChange={(e) => handleInputChange({ target: { name: 'next_booking_discount_type', value: e.target.value } })}
                      className="rounded-md border border-[hsl(var(--border))] px-3 py-2 text-sm bg-white"
                      data-testid="next-discount-type"
                    >
                      <option value="percent">Prozent (%)</option>
                      <option value="fixed">Fix (CHF)</option>
                    </select>
                    <Input
                      type="number"
                      min="0"
                      value={settings.next_booking_discount_value}
                      onChange={(e) => handleInputChange({ target: { name: 'next_booking_discount_value', value: parseFloat(e.target.value) || 0 } })}
                      className="w-24"
                      data-testid="next-discount-value"
                    />
                    <span className="text-sm text-[hsl(var(--muted-foreground))]">
                      {settings.next_booking_discount_type === 'percent' ? '%' : 'CHF'}
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Referral Discount Settings */}
          <Card className="card-shadow mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Share2 className="w-5 h-5 text-blue-500" />
                Freunde-werben-Rabatt
              </CardTitle>
              <CardDescription>
                Kunden können einen Empfehlungslink an Freunde teilen. Beide erhalten einen Rabatt.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Empfehlungsprogramm aktivieren</Label>
                </div>
                <button
                  type="button"
                  onClick={() => handleInputChange({ target: { name: 'referral_discount_enabled', value: !settings.referral_discount_enabled } })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.referral_discount_enabled ? 'bg-[hsl(var(--primary))]' : 'bg-[hsl(var(--muted))]'
                  }`}
                  data-testid="settings-referral-toggle"
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.referral_discount_enabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
              {settings.referral_discount_enabled && (
                <div className="pt-3 border-t border-[hsl(var(--border))] space-y-4">
                  <div>
                    <Label className="text-sm font-medium block mb-2">Belohnung für den Werber</Label>
                    <div className="flex items-center gap-3">
                      <select
                        value={settings.referral_discount_type}
                        onChange={(e) => handleInputChange({ target: { name: 'referral_discount_type', value: e.target.value } })}
                        className="rounded-md border border-[hsl(var(--border))] px-3 py-2 text-sm bg-white"
                        data-testid="referral-reward-type"
                      >
                        <option value="percent">Prozent (%)</option>
                        <option value="fixed">Fix (CHF)</option>
                      </select>
                      <Input type="number" min="0" value={settings.referral_discount_value}
                        onChange={(e) => handleInputChange({ target: { name: 'referral_discount_value', value: parseFloat(e.target.value) || 0 } })}
                        className="w-24" data-testid="referral-reward-value" />
                      <span className="text-sm text-[hsl(var(--muted-foreground))]">{settings.referral_discount_type === 'percent' ? '%' : 'CHF'}</span>
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium block mb-2">Rabatt für den eingeladenen Freund</Label>
                    <div className="flex items-center gap-3">
                      <select
                        value={settings.referral_friend_discount_type}
                        onChange={(e) => handleInputChange({ target: { name: 'referral_friend_discount_type', value: e.target.value } })}
                        className="rounded-md border border-[hsl(var(--border))] px-3 py-2 text-sm bg-white"
                        data-testid="referral-friend-type"
                      >
                        <option value="percent">Prozent (%)</option>
                        <option value="fixed">Fix (CHF)</option>
                      </select>
                      <Input type="number" min="0" value={settings.referral_friend_discount_value}
                        onChange={(e) => handleInputChange({ target: { name: 'referral_friend_discount_value', value: parseFloat(e.target.value) || 0 } })}
                        className="w-24" data-testid="referral-friend-value" />
                      <span className="text-sm text-[hsl(var(--muted-foreground))]">{settings.referral_friend_discount_type === 'percent' ? '%' : 'CHF'}</span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Company Info Settings */}
          <Card className="card-shadow mb-6">
            <CardHeader>
              <CardTitle>Firmeninformationen</CardTitle>
              <CardDescription>
                Diese Informationen werden auf der Webseite und in E-Mails angezeigt.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="company_name">{t('admin.companyName')}</Label>
                <Input
                  id="company_name"
                  name="company_name"
                  value={settings.company_name}
                  onChange={handleInputChange}
                  placeholder="ZO Werbeflächen"
                  data-testid="settings-company-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company_email">{t('admin.companyEmail')}</Label>
                <Input
                  id="company_email"
                  name="company_email"
                  type="email"
                  value={settings.company_email}
                  onChange={handleInputChange}
                  placeholder="info@trucksonroad.ch"
                  data-testid="settings-company-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company_phone">{t('admin.companyPhone')}</Label>
                <Input
                  id="company_phone"
                  name="company_phone"
                  value={settings.company_phone}
                  onChange={handleInputChange}
                  placeholder="+41 79 696 98 99"
                  data-testid="settings-company-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company_address">{t('admin.companyAddress')}</Label>
                <Input
                  id="company_address"
                  name="company_address"
                  value={settings.company_address}
                  onChange={handleInputChange}
                  placeholder="Bahnhofstrasse 75, 8620 Wetzikon"
                  data-testid="settings-company-address-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="whatsapp_number">WhatsApp-Nummer</Label>
                <Input
                  id="whatsapp_number"
                  name="whatsapp_number"
                  value={settings.whatsapp_number}
                  onChange={handleInputChange}
                  placeholder="+41796969899"
                  data-testid="settings-whatsapp-input"
                />
                <p className="text-xs text-[hsl(var(--muted-foreground))]">Format: +41796969899 (ohne Leerzeichen). Wird als WhatsApp-Button auf allen Seiten angezeigt.</p>
              </div>
            </CardContent>
          </Card>
    </>
  );
};

export default SettingsMarketing;
